import { dark, shadcn } from "@clerk/themes";
import { useTheme } from "@/components/theme-provider";
import { METALUI_BRAND } from "@workspace/brand";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

// MetalUI platform shell. The Clerk widget renders on the pre-auth
// sign-in/sign-up pages where no tenant is bound yet, so it defaults to the
// platform brand (MetalUI navy + cyan). On a `<slug>.metalui.co.uk` subdomain
// we resolve the tenant's logo from the per-slug localStorage cache (written
// by BrandingProvider's pre-auth fetch) so the Clerk card logo also reflects
// the tenant's brand on subdomain visits.
//
// Known cosmetic carryover: post-auth Clerk surfaces (UserButton dropdown,
// UserProfile modal) show MetalUI navy instead of each tenant's brand colors.
// These are small, low-traffic surfaces — the main app shell (sidebar,
// topbar, dashboard) is unaffected because it reads from BrandingProvider's
// tenant-resolved palette.

// ---------------------------------------------------------------------------
// Subdomain logo resolution for the Clerk widget
// ---------------------------------------------------------------------------
// `useClerkAppearance` is called in `ClerkProviderWithRoutes` (App.tsx), which
// is mounted OUTSIDE `BrandingProvider`. We therefore resolve the subdomain
// logo independently by reading the same per-slug localStorage cache that
// `BrandingProvider` writes to after its pre-auth fetch.
//
// Cache key format mirrors `vc-subdomain-branding-v1:<slug>` in
// `branding-provider.tsx`. Keep these in sync if the prefix ever changes.

const SUBDOMAIN_CACHE_PREFIX = "vc-subdomain-branding-v1";

function resolveSubdomainSlug(): string | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname.toLowerCase();
  const suffix = `.${METALUI_BRAND.platformDomain}`;
  if (!hostname.endsWith(suffix)) return null;
  const slug = hostname.slice(0, hostname.length - suffix.length);
  return slug && /^[a-z0-9-]+$/.test(slug) ? slug : null;
}

/**
 * Read the tenant logo URL from the per-slug subdomain branding cache.
 * Returns null when:
 *  - not on a metalui.co.uk subdomain
 *  - cache has no entry yet (first visit before BrandingProvider has fetched)
 *  - cached logo is a private /objects/ path (not fetchable without a session)
 *
 * Called inside `useClerkAppearance` on every render so Clerk picks up the
 * logo as soon as BrandingProvider writes it to localStorage on first visit.
 */
function readSubdomainLogoUrl(): string | null {
  const slug = resolveSubdomainSlug();
  if (!slug) return null;
  try {
    const raw = window.localStorage.getItem(`${SUBDOMAIN_CACHE_PREFIX}:${slug}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { logoUrl?: string | null };
    const logoUrl = parsed.logoUrl;
    if (!logoUrl) return null;
    // Only return publicly-fetchable logos (absolute URL or tenant-public
    // object). Private /objects/ paths require a session and would show a
    // broken image in the Clerk card header.
    const isPublic =
      /^https?:\/\//i.test(logoUrl) || logoUrl.startsWith("/public-objects/");
    if (!isPublic) return null;
    // Resolve tenant-public paths to their API URL.
    if (logoUrl.startsWith("/public-objects/")) {
      return `${window.location.origin}${basePath}/api/storage/public${logoUrl.slice("/public-objects".length)}`;
    }
    return logoUrl;
  } catch {
    return null;
  }
}

const lightVariables = {
  colorPrimary: "#1E3A8A",
  colorForeground: "#1E3A8A",
  colorMutedForeground: "#5C6B82",
  colorDanger: "#C72A1F",
  colorBackground: "#FFFFFF",
  colorInput: "#FFFFFF",
  colorInputForeground: "#1E3A8A",
  colorNeutral: "#CBD5E1",
  fontFamily: "Inter, system-ui, sans-serif",
  borderRadius: "0.375rem",
};

const darkVariables = {
  colorPrimary: "#D4AF37",
  colorForeground: "#F1F5F9",
  colorMutedForeground: "#94A3B8",
  colorDanger: "#E5544A",
  colorBackground: "#0F1115",
  colorInput: "#1A1D24",
  colorInputForeground: "#F1F5F9",
  colorNeutral: "#4B5563",
  fontFamily: "Inter, system-ui, sans-serif",
  borderRadius: "0.375rem",
};

const lightElements = {
  rootBox: "w-full",
  cardBox:
    "bg-white border border-[#CBD5E1] shadow-2xl rounded-2xl w-[440px] max-w-full overflow-hidden",
  card: "!shadow-none !border-0 !bg-transparent !rounded-none",
  footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
  headerTitle: "text-[#1E3A8A] font-sans font-bold text-2xl",
  headerSubtitle: "text-[#5C6B82]",
  socialButtonsBlockButton:
    "border border-[#CBD5E1] bg-white hover:bg-[#F1F5F9] text-[#1E3A8A]",
  socialButtonsBlockButtonText: "text-[#1E3A8A] font-medium",
  formButtonPrimary:
    "bg-[#1E3A8A] hover:bg-[#16306E] text-white font-semibold",
  formFieldLabel: "text-[#1E3A8A] font-medium",
  formFieldInput:
    "border border-[#CBD5E1] bg-white text-[#1E3A8A] placeholder:text-[#94A3B8]",
  formFieldSuccessText: "text-[#1F7A3A]",
  footerAction: "bg-transparent",
  footerActionText: "text-[#5C6B82]",
  footerActionLink: "text-[#1E3A8A] hover:text-[#16306E] font-medium",
  dividerLine: "bg-[#CBD5E1]",
  dividerText: "text-[#5C6B82]",
  identityPreviewEditButton: "text-[#1E3A8A] hover:text-[#16306E]",
  alert: "border border-[#CBD5E1] bg-[#F1F5F9]",
  alertText: "text-[#1E3A8A]",
  otpCodeFieldInput: "border border-[#CBD5E1] bg-white text-[#1E3A8A]",
  formFieldRow: "gap-2",
  main: "gap-4",
  logoBox: "py-4",
  logoImage: "h-12",
};

const darkElements = {
  rootBox: "w-full",
  cardBox:
    "bg-[#0F1115] border border-[#4B5563] shadow-2xl rounded-2xl w-[440px] max-w-full overflow-hidden",
  card: "!shadow-none !border-0 !bg-transparent !rounded-none",
  footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
  headerTitle: "text-[#F1F5F9] font-sans font-bold text-2xl",
  headerSubtitle: "text-[#94A3B8]",
  socialButtonsBlockButton:
    "border border-[#4B5563] bg-[#1A1D24] hover:bg-[#4B5563] text-[#F1F5F9]",
  socialButtonsBlockButtonText: "text-[#F1F5F9] font-medium",
  formButtonPrimary:
    "bg-[#D4AF37] hover:bg-[#BF9B2E] text-[#0F1115] font-semibold",
  formFieldLabel: "text-[#F1F5F9] font-medium",
  formFieldInput:
    "border border-[#4B5563] bg-[#1A1D24] text-[#F1F5F9] placeholder:text-[#64748B]",
  formFieldSuccessText: "text-[#5BC07A]",
  footerAction: "bg-transparent",
  footerActionText: "text-[#94A3B8]",
  footerActionLink: "text-[#D4AF37] hover:text-[#E4C567] font-medium",
  dividerLine: "bg-[#4B5563]",
  dividerText: "text-[#94A3B8]",
  identityPreviewEditButton: "text-[#D4AF37] hover:text-[#E4C567]",
  alert: "border border-[#4B5563] bg-[#1A1D24]",
  alertText: "text-[#F1F5F9]",
  otpCodeFieldInput: "border border-[#4B5563] bg-[#1A1D24] text-[#F1F5F9]",
  formFieldRow: "gap-2",
  main: "gap-4",
  logoBox: "py-4",
  logoImage: "h-12",
};

export function useClerkAppearance() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Resolve the Clerk widget logo: use the tenant's cached subdomain logo
  // (if publicly accessible) on a metalui.co.uk subdomain, else the MetalUI
  // platform favicon. Called on every render so the logo updates as soon as
  // BrandingProvider writes the first-fetch result to localStorage.
  const tenantLogoUrl = readSubdomainLogoUrl();
  // Platform fallback shield, picked to read against the Clerk card surface:
  // white on the dark-theme card, black on the light-theme card.
  const platformShield = isDark
    ? METALUI_BRAND.shieldFileWhite
    : METALUI_BRAND.shieldFileBlack;
  const logoImageUrl =
    tenantLogoUrl ??
    (typeof window !== "undefined"
      ? `${window.location.origin}${basePath}/${platformShield}`
      : `/${platformShield}`);

  return {
    theme: isDark ? dark : shadcn,
    cssLayerName: "clerk",
    options: {
      logoPlacement: "inside" as const,
      logoLinkUrl: basePath || "/",
      logoImageUrl,
      socialButtonsPlacement: "top" as const,
      socialButtonsVariant: "blockButton" as const,
    },
    variables: isDark ? darkVariables : lightVariables,
    elements: isDark ? darkElements : lightElements,
  };
}
